import {
  Activity,
  Building2,
  ClipboardList,
  Database,
  FileText,
  Folder,
  Key,
  LayoutDashboard,
  MessageSquare,
  Shield,
  Users,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Feature flags
// Static flags controlling UI behaviour across features.
// ---------------------------------------------------------------------------
export const FEATURE_FLAGS = {
  /** Show the Metrics by Campaign / Ad Group table in the Reports page */
  SHOW_METRICS_BY_CAMPAIGN_AD_GROUP: false,
  /** Show the Metrics by Campaign / Ad Group / Ad table in the Reports page */
  SHOW_METRICS_BY_CAMPAIGN_AD: true,
} as const;

// ---------------------------------------------------------------------------
// Default start pages per user scope
// Update here to change post-login / post-context-switch destinations.
// ---------------------------------------------------------------------------
export const DEFAULT_START_PAGES: Record<string, string> = {
  platform: "/app/analytics-dashboard",
  agency: "/app/reports",
  tenant: "/app/agency-console",
  org: "/app/my-reports",
} as const;

// ---------------------------------------------------------------------------
// Scope-based theme colors
// Single source of truth for per-scope brand colors.
// Mirror any changes here in src/styles/theme.css (CSS cannot read JS constants).
// Keys map to User.scope values: "platform" | "tenant" | "org"
// ---------------------------------------------------------------------------
export const SCOPE_THEME_COLORS = {
  platform: {
    primary: "#78c5ca",
    primaryHover: "#6ab5ba",
    secondary: "#ed9b3f",
    secondaryHover: "#e08a2e",
  },
  tenant: {
    primary: "#7c82e8",
    primaryHover: "#6b72df",
    secondary: "#ed9b3f",
    secondaryHover: "#e08a2e",
  },
  org: {
    primary: "#9b6be8",
    primaryHover: "#8a5bd8",
    secondary: "#ed9b3f",
    secondaryHover: "#e08a2e",
  },
} as const satisfies Record<
  string,
  {
    primary: string;
    primaryHover: string;
    secondary: string;
    secondaryHover: string;
  }
>;

// ---------------------------------------------------------------------------
// Validation patterns
// Centralised regex patterns for form validation across all portal components.
// All text patterns include Japanese script ranges:
//   \u3040-\u309F  Hiragana
//   \u30A0-\u30FF  Katakana
//   \u4E00-\u9FFF  CJK Unified Ideographs (kanji)
//   \uFF00-\uFFEF  Fullwidth & Halfwidth Forms
// ---------------------------------------------------------------------------
export const VALIDATION_PATTERNS = {
  /** Person names – letters (Latin + Japanese), spaces, hyphens, apostrophes, periods */
  PERSON_NAME:
    /^[a-zA-Z\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uFF00-\uFFEF .'-]+$/,
  /** Entity / company names – same as above plus numbers, underscores, ampersands, parens */
  ENTITY_NAME:
    /^[a-zA-Z0-9\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uFF00-\uFFEF \-_.&()]+$/,
  /** Role descriptions – letters + Japanese, numbers, spaces (no special chars) */
  ALPHANUMERIC_JP:
    /^[a-zA-Z0-9\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uFF00-\uFFEF ]+$/,
  /** Strip pattern (inverse of ALPHANUMERIC_JP) – used in onInput handlers to remove invalid chars */
  STRIP_NON_ALPHANUMERIC_JP:
    /[^a-zA-Z0-9\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uFF00-\uFFEF ]/g,
  /** Standard email address */
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  /** International phone number */
  PHONE:
    /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/,
  /** Website / URL */
  WEBSITE: /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\/[^\s]*)?$/,
  /** Password: min 8 chars, requires uppercase, lowercase, number, and special char */
  PASSWORD_STRENGTH:
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/,
} as const;

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Default sync schedule
// Used by ConnectionWizard (new connection) and SchedulePicker (fallback).
// Change these values here — they will be reflected everywhere automatically.
// ---------------------------------------------------------------------------
export const DEFAULT_SYNC_SCHEDULE = {
  /** Cron expression: daily at 19:00 UTC (7 PM UTC) */
  cron_expression: "0 19 * * *",
  /** IANA timezone identifier */
  timezone: "UTC",
  /** Sync is always enabled; the UI shows fields locked until user opts to change them */
  is_enabled: true,
  /** Frequency parsed from cron_expression — used to pre-select the frequency pill */
  frequency: "daily" as const,
  /** 24-hour value of the default run hour */
  hour24: 19,
  /** Minute value of the default run time */
  minute: 0,
} as const;

// ---------------------------------------------------------------------------
// Permission dependency map
// "resource.action" → ["required resource.action", ...]
// Matched against permission.key or computed "resource.action"
// ---------------------------------------------------------------------------
export const PERMISSION_DEPENDENCIES: Record<string, string[]> = {
  "users.create": ["users.read", "roles.read"],
  "users.update": ["users.read", "roles.read"],
  "roles.create": ["roles.read", "permissions.read"],
  "roles.update": ["roles.read", "permissions.read"],
};

// ---------------------------------------------------------------------------
// Navigation sections
// Exported as a factory function so translation keys can be resolved at
// render time via the t() function passed in from the component.
// ---------------------------------------------------------------------------
export const getNavigationSections = (t: (key: string) => string) => [
  {
    id: "application",
    title: t("nav.application"),
    items: [
      {
        id: "onboarding-assist",
        path: "/app/onboarding-assist",
        label: t("nav.onboardingAssist"),
        icon: ClipboardList,
        permissions: ["csv.create"],
      },
      {
        id: "agency-console",
        path: "/app/agency-console",
        label: t("agencyConsole.title"),
        icon: LayoutDashboard,
        permissions: ["organizations.read"], // demo design — visible to all authenticated users
      },
      {
        id: "analytics-dashboard",
        path: "/app/analytics-dashboard",
        label: t("platformAdmin.title"),
        icon: Shield,
        permissions: ["agencies.read"],
      },
      {
        id: "reports",
        path: "/app/reports",
        label: t("nav.reports"),
        icon: FileText,
        permissions: ["reports.read", "reports.read.org"],
      },
      {
        id: "data-sources",
        path: "/app/data-sources",
        label: t("nav.dataSources"),
        icon: Database,
        permissions: ["syncs.read"],
      },
      {
        id: "my-reports",
        path: "/app/my-reports",
        label: t("nav.myReports"),
        icon: Folder,
        permissions: ["reports.read"],
      },
      {
        id: "prompt",
        path: "/app/prompt",
        label: t("nav.prompts"),
        icon: MessageSquare,
        permissions: ["prompts.read"],
      },
    ],
  },
  {
    id: "administration",
    title: t("nav.administration"),
    items: [
      {
        id: "dashboard",
        path: "/app/dashboard",
        label: t("nav.dashboard"),
        icon: LayoutDashboard,
        permissions: ["dashboards.read"],
      },
      {
        id: "users",
        path: "/app/users",
        label: t("nav.users"),
        icon: Users,
        permissions: ["users.read"],
      },
      {
        id: "agencies",
        path: "/app/agencies",
        label: t("nav.agencies"),
        icon: Building2,
        permissions: ["agencies.read"],
      },
      {
        id: "organizations",
        path: "/app/organizations",
        label: t("nav.organizations"),
        icon: Activity,
        permissions: ["organizations.read"],
      },
      {
        id: "roles",
        path: "/app/roles",
        label: t("nav.roles"),
        icon: Shield,
        permissions: ["roles.read"],
      },
      {
        id: "permissions",
        path: "/app/permissions",
        label: t("nav.permissions"),
        icon: Key,
        permissions: ["roles.read"],
      },
    ],
  },
];
